<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'TlbsModel',
  
})
</script>
<template>
  <div
    ref="tmodelContainer"
    class="tlbs-model"
  />
</template>
<script setup lang="ts" name="TlbsModel">
import TModel from '@tlbs/galaxy-model';
import updatedDiff from '../utils/objectDiff';
import { defaultConfig } from './model.config';
import debounce from 'lodash/debounce';

interface ModelState {
  currentScene: number;
  sceneArray: ModelSceneState[];
}

interface CameraOption {
  position?: number[];
  target?: number[];
  minDistance?: number;
  maxDistance?: number;
  duration?: number;
}

interface CirCleOptions {
  color?: string;
  innerRadius: number;
  outerRadius: number;
  position: number[];
  rotation: number[];
}

interface FlowLightRange {
  x: number;
  y: number;
}

interface LineOptions {
  color?: string;
  end: number[];
  gapsize: number;
  start: number[];
  type: string;
}

interface RingOptions {
  color?: string;
  position: number[];
  radius: number;
  rotation: number[];
  type: string;
}

interface TextOptions {
  color?: string;
  content?: string;
  font?: string;
  fontSize: number;
  height?: number;
  position: number[];
  rotation: number[];
}

interface ModelSceneState {
  animation?: {
    dest?: number[];
    rotation?: number[];
    duration?: number;
    easeType: string;
    enablePositionAnimation: boolean;
    enableAnimation: boolean;
    target?: any
  };
  autoRotate?: boolean;
  background?: string;
  camera: {
    maxDistance: number;
    minDistance: number;
    position: number[];
    target: number[];
  };
  circle: CirCleOptions[];
  enableEnv: boolean;
  light: LightOptions[];
  flowLight: {
    useFlowLight: boolean;
    targetUrl: string;
    materialMixUrl: string;
    duration: number;
    color: string;
    range: FlowLightRange[]
  }
  line: LineOptions[];
  model: ModelInstanceOption;
  polarAngle: number;
  ring: RingOptions[];
  text: TextOptions[];
}

interface Props {
  sceneArray: any,
  currentScene: number,
}

const props = withDefaults(defineProps<Props>(), {
  sceneArray: [defaultConfig],
  currentScene: 1,
});

const emit = defineEmits<{
  (e: 'loading', progress: number): void,
  (e: 'loaded'): void,
}>();

const tmodelContainer = ref<HTMLElement>();

onMounted(() => {
  nextTick(() => {
    initModel();
  });
});

let sceneInstance: any; let modelInstance: any; let storeConfig: ModelSceneState;let currentIndex: number;
const initModel = (): void => {
  sceneInstance = new TModel.Scene(tmodelContainer.value);
  debounceChange(props);
};

// TODO: 目前把设置模型位置和相机lookat都给关了
const onDataChange = (newProps: ModelState) => {
  if (!sceneInstance) return;
  const currentConfig = newProps.sceneArray[newProps.currentScene - 1];
  if (!currentConfig) return;
  const isChangeScene = !(newProps.currentScene === currentIndex);
  const diffConfig = updatedDiff(storeConfig, currentConfig);
  const diffConfigAdd = updatedDiff(currentConfig, storeConfig);
  const { model, light, line, circle, text, camera, ring, enableEnv, flowLight } = diffConfig;
  (isChangeScene || camera) && updateCamera(currentConfig?.camera, storeConfig?.camera);
  light && updateLight(currentConfig?.light);
  ((model || diffConfigAdd.model || flowLight || diffConfigAdd.flowLight) || isChangeScene) && updateModel(
    currentConfig?.model,
    { flowLight: currentConfig?.flowLight,
      rotation: currentConfig?.animation?.rotation,
      animation: currentConfig?.animation,
      autoRotate: currentConfig?.autoRotate,
    },
  );
  if (currentConfig.animation && isChangeScene && modelInstance.object && currentConfig?.animation?.enableAnimation) {
    sceneInstance.easeTo(
      Object.assign(currentConfig?.animation, { target: modelInstance.object }),
      currentConfig?.animation?.enablePositionAnimation,
    );
  } else if (model && isChangeScene) {
    currentConfig.model.position && modelInstance.setPosition(currentConfig.model.position);
  }
  sceneInstance.setBackground(currentConfig?.background || '');
  if (line || diffConfigAdd.line) {
    sceneInstance.clearLine();
    currentConfig.line?.forEach((singleLine: LineOptions) => {
      sceneInstance.addLine(singleLine);
    });
  }
  if (text || diffConfigAdd.text) {
    sceneInstance.clearText();
    currentConfig.text?.forEach((singleText: TextOptions) => {
      const formatText = {
        content: singleText.content,
        position: singleText.position,
        rotation: singleText.rotation,
        style: { ...singleText },
      };
      sceneInstance.addText(formatText);
    });
  }
  if (circle || diffConfigAdd?.circle) {
    sceneInstance.clearCircle();
    currentConfig.circle?.forEach((singleCircle: CirCleOptions) => {
      sceneInstance.addCircle(singleCircle);
    });
  }
  if (ring || diffConfigAdd.ring) {
    sceneInstance.clearRing();
    currentConfig.ring?.forEach((singleRing: RingOptions) => {
      sceneInstance.addRing(singleRing);
    });
  }
  enableEnv !== undefined && sceneInstance.updateEnv(enableEnv);
  currentConfig.polarAngle && sceneInstance.setPolarAngel(currentConfig.polarAngle);
  storeConfig = currentConfig;
  currentIndex = newProps.currentScene;
};

const debounceChange = debounce(onDataChange, 100);
watch(props, debounceChange);

const runAnimation = (currentConfig: any) => {
  // 执行动画时关闭自动旋转
  sceneInstance.setAutoRotate(false);
  if (currentConfig.animation && currentConfig?.animation?.enableAnimation) {
    const setAutoRotate = () => {
      sceneInstance.setAutoRotate(currentConfig?.autoRotate);
    };
    sceneInstance.easeTo(
      Object.assign(currentConfig?.animation, { target: modelInstance.object }),
      currentConfig?.animation?.enablePositionAnimation,
      setAutoRotate,
    );
  } else {
    sceneInstance.setAutoRotate(currentConfig?.autoRotate);
  }
};

const updateModel = (model: ModelInstanceOption, modelConfig?: any): void => {
  const { url,  scale, position, rotation } = model;
  const currentUrl = modelInstance?.url;
  if (url && url === currentUrl) {
    scale && modelInstance.setScale(scale);
    position && modelInstance.setPosition(position);
    rotation && modelInstance.setRotation(rotation);
    modelInstance.useFlowLight(modelConfig?.flowLight?.useFlowLight);
    modelInstance.addFlowLight({ ...modelConfig.flowLight });
    runAnimation(modelConfig);
  } else {
    // 初始化时，模型rotation 为动画的rotation
    const modelOption = { ...model, ...modelConfig.flowLight };
    if (modelOption && modelConfig?.animateRotation) {
      modelOption.rotation = modelConfig.animateRotation;
    }
    modelInstance = new TModel.Model(modelOption, sceneInstance);
    modelInstance.on('loaded', (): void => {
      sceneInstance.addModel(modelInstance);
      emit('loaded');
      if (modelConfig.flowLight?.targetUrl) {
        modelInstance.addFlowLight({ ...modelConfig.flowLight });
        // color: '#E7C474',
        //  laiding range: [{ x: 0.065512, y: 0.69352 }, { x: 0.24021, y: 0.80873 }],
        // 牛股range: [{ x: 0.84623, y: 0.3322 }, { x: 0.5193, y: 0.6989 }],
      }
      const currentConfig = props.sceneArray[props.currentScene - 1];
      runAnimation(currentConfig);
    });
    modelInstance.useFlowLight(modelConfig?.flowLight?.useFlowLight);
    modelInstance.on('loading', (evt: any): void => {
      emit('loading', evt.progress);
    });
  }
};

const updateCamera = (camera: CameraOption, oldCameraOption: CameraOption | null): void => {
  const { position, target, minDistance, maxDistance, duration = 2000 } = camera;
  position && sceneInstance.updateCamera({ position, lookAt: target, minDistance, maxDistance,
    animation: {
      // type: '',
      initPosition: oldCameraOption?.position,
      duration,
    } });
};

const updateLight = (lightArray: LightOptions[]): void => {
  sceneInstance.removeLights();
  if (!Array.isArray(lightArray)) return;
  lightArray.forEach((lightOption: LightOptions): void => {
    sceneInstance.addLight(lightOption);
  });
};

</script>
